/** TradingView Strategy Tester CSV import lane (`broker_name: tradingview`). */

export const TRADINGVIEW_BROKER_SLUG = "tradingview";

function normalizeTvAssetClasses(classes: string[]): string[] {
  return classes.map((c) => c.trim().toLowerCase()).filter(Boolean);
}

export const TRADINGVIEW_DEFAULT_ASSET_CLASSES = [
  "commodity",
  "crypto",
  "energy",
  "equity",
  "fx",
  "index",
  "metal",
] as const;

export function isTradingViewBroker(brokerName: string | null | undefined): boolean {
  return String(brokerName || "").toLowerCase() === TRADINGVIEW_BROKER_SLUG;
}

export function isTradedAssetClassesField(field: string): boolean {
  const lower = field.toLowerCase();
  return lower === "traded_asset_classes" || lower === "tradedassetclasses";
}

export { isExecutingBrokerNameField } from "./brokerExecutingBroker";

/** Hidden on the form — always sent as `tradingview` on import. */
export function isTradingViewHiddenCsvField(field: string): boolean {
  return field.toLowerCase() === "broker_name";
}

const ASSET_CLASS_LABEL_OVERRIDES: Record<string, string> = {
  fx: "Forex",
};

export function formatAssetClassLabel(slug: string): string {
  const t = slug.trim().toLowerCase();
  if (!t) return slug.trim();
  if (ASSET_CLASS_LABEL_OVERRIDES[t]) return ASSET_CLASS_LABEL_OVERRIDES[t];
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** User-facing CSV field labels — overrides technical API `field_labels`. */
export const TRADINGVIEW_CSV_FIELD_LABELS: Record<string, string> = {
  account_name: "Account name",
  instrument: "Instrument",
  time_zone: "Chart timezone",
  traded_asset_classes: "Asset class",
  executing_broker_name: "Executing broker",
};

export function tradingViewCsvFieldLabel(field: string, fallback: string): string {
  return TRADINGVIEW_CSV_FIELD_LABELS[field] ?? fallback;
}

/**
 * POST /broker/credentials for TradingView requires `account_name`.
 * `traded_asset_classes` is optional at connect/import — per-symbol setup runs after sync.
 */
export function canPreSaveTradingViewBrokerCredentials(
  values: Record<string, string>,
  _assetClasses: string[] = []
): boolean {
  const accountName = (values.account_name || values.accountName || "").trim();
  return Boolean(accountName);
}

/** Asset classes for query `traded_asset_classes` (required on POST …/tradingview/csv-import). */
export function resolveTradingViewCsvAssetClasses(
  values: Record<string, string>,
  selectedClasses: string[],
  fixedValues?: string[] | null
): string[] {
  const fromPicker = normalizeTvAssetClasses(selectedClasses);
  if (fromPicker.length > 0) return fromPicker;
  const single = (values.traded_asset_classes || values.tradedAssetClasses || "").trim();
  if (single) return [single.toLowerCase()];
  if (fixedValues?.length) return normalizeTvAssetClasses(fixedValues);
  return [];
}

export function buildTradingViewCsvImportParams(
  values: Record<string, string>,
  assetClasses: string[] = []
): Record<string, string | string[]> {
  const accountName = (values.account_name || values.accountName || "").trim();
  const instrument = (values.instrument || "").trim().toUpperCase();
  const timeZone = (values.time_zone || values.timeZone || "").trim();
  const executingBrokerName = (
    values.executing_broker_name ||
    values.executingBrokerName ||
    ""
  ).trim();
  const params: Record<string, string | string[]> = {
    broker_name: TRADINGVIEW_BROKER_SLUG,
    account_name: accountName,
    instrument,
    time_zone: timeZone,
  };
  const tradedAssetClasses = resolveTradingViewCsvAssetClasses(values, assetClasses);
  if (tradedAssetClasses.length > 0) {
    params.traded_asset_classes = tradedAssetClasses;
  }
  if (executingBrokerName) {
    params.executing_broker_name = executingBrokerName;
  }
  return params;
}
